import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { Box, InputAdornment } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { createUser, updateUserProfile, editUserProfileInfo, logoutClear } from "../../redux/actions/appActions";
import { ROUTE_WELCOME, ROUTE_COMPLETE_PROFILE, ROUTE_MAIN } from "../../constants/routes";
import { useEffect, useState } from "react";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

type FormValues = {
  fullName: string;
  // gender: string;
  email: string;
};

const schema = Yup.object().shape({
  fullName: Yup.string().required("Full name is required"),
  // gender: Yup.string().required("Please select your gender"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
});

const CssTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
      boxShadow: `inset 0px 0px 11px 2px #4B44D4`,
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& input": {
    color: "white",
    border: "none",
    borderRadius: "99px",
  },
  "& input::placeholder": {
    color: "#fff",
    fontSize: "18px",
  },
  "& label": {
    color: "white !important",
  },
  "& .MuiInputLabel-outlined": {
    transform: "translate(14px, 12px) scale(1)",
  },
}));

const WhatYourName = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appState = useSelector((state: any) => state.app);
  const userProfile = useSelector((state: any) => state.app);
  const [isEditMode, setIsEditMode] = useState(false);

  const Logout = () => {
    dispatch(logoutClear(() => {
      navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const defaultValues = {
    fullName: "",
    // gender: "",
    email: "",
  };

  useEffect(() => {
    if (location?.search?.includes("edit")) {
      setIsEditMode(true);
    }
  }, []);

  const { handleSubmit, control, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  // Pre-populate form when in edit mode and userProfile is loaded
  useEffect(() => {
    if (isEditMode && userProfile && Object.keys(userProfile).length > 0) {
      const { fullName, email } = userProfile?.user?.user;
      setValue("fullName", fullName || "");
      setValue("email", email || "");
    }
  }, [isEditMode, userProfile, setValue]);

  const createNewUser = (data: FormValues) => {
    const params = new URLSearchParams(location.search);
    const payload = {
      // gender: data?.gender,
      fullName: data?.fullName,
      phoneNumber: appState?.user?.phoneNumber,
      city: params.get("city"),
    };
    dispatch(createUser(payload, () => navigate(ROUTE_WELCOME)));
  };

  const onSubmit = (data: FormValues) => {
    const payload = {
      fullName: data?.fullName,
      // gender: data?.gender,
      email: data?.email,
    };
    if (isEditMode) {
      dispatch(editUserProfileInfo(payload, () => navigate(ROUTE_COMPLETE_PROFILE)));
    } else {
      dispatch(updateUserProfile(payload, () => createNewUser(data)));
    }
  };

  return (
    <section className="signup-bg" style={{ overflow: "hidden", height: "100vh" }}>
      {/* Header with Home and Logout buttons - only shown in edit mode */}
      {isEditMode && (
        <Box
          sx={{
            display: "flex",
            backgroundColor: "rgb(255, 137, 96)",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
            <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
          </Header>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
              <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
                <HomeIcon />
              </InputAdornment>
            </Box>
            <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
              <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
                <LogoutIcon />
              </InputAdornment>
            </Box>
          </Box>
        </Box>
      )}
      <span className="round-one"></span>
      <span className="round-two"></span>
      <span className="round-three"></span>
      <span className="round-four"></span>
      <span className="round-five"></span>
      <span className="round-six"></span>
      <span className="round-seven"></span>
      <span className="round-eight"></span>
      <span className="round-nine"></span>
      <span className="round-ten"></span>
      <span className="round-eleven"></span>
      <Container maxWidth="md" sx={{ position: "relative", zIndex: "99" }} className="m-0 p-0">
        {!isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Box
              component="img"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/2e88e2fbec7ace948fcde95ec056e8b760fc06d3dfba3d5d3f7fa80729a3c10b?apiKey=80f439f9d23243918de9eba558cc207a&"
              alt=""
              sx={{
                alignSelf: "center",
                aspectRatio: "1.92",
                width: "94px",
              }}
            />
          </Box>
        )}
        <Box className="bg-white roundedten pad-desktop-signup-1">
          <div className="text-center mt-5 mb-5">
            <h2 className="text-white" style={{ fontSize: "35px" }}>
              {isEditMode ? "" : "Great!"}
            </h2>
            <h2 className="text-white mt-1 name-heading">
              {isEditMode ? "Edit Your Profile" : "What is Your Name??"}
            </h2>
          </div>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <div className="mx-lg-5">
              <Stack direction="row" justifyContent="end">
                <img src="/images/name.png" alt="" className="img-fluid me-5" style={{ width: "100px" }} />
              </Stack>
              <Controller
                control={control}
                name="fullName"
                render={({ field, fieldState: { error } }) => (
                  <CssTextField
                    {...field}
                    error={!!error}
                    helperText={error?.message || " "}
                    id="outlined-basic"
                    placeholder="Full Name"
                    variant="outlined"
                    className="w-100 text-white"
                    InputProps={{
                      style: {
                        borderRadius: "99px",
                        backgroundColor: "#6861E3",
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <CssTextField
                    {...field}
                    error={!!error}
                    helperText={error?.message || " "}
                    id="outlined-basic"
                    placeholder="Email"
                    variant="outlined"
                    className="w-100 text-white"
                    InputProps={{
                      style: {
                        borderRadius: "99px",
                        backgroundColor: "#6861E3",
                      },
                    }}
                  />
                )}
              />
            </div>

            <div className="mx-lg-5">
              <Button
                sx={{
                  backgroundColor: "#fff",
                  padding: "15px 0px",
                  borderRadius: "99px",
                  boxShadow: `0px 0px 11px 2px #4B44D4`,
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                  color: "#756DE9",
                  fontSize: "16px",
                  fontWeight: "600",
                  textTransform: "capitalize",
                }}
                className="w-100 mt-4 fontP mb-2"
                variant="text"
                type="submit"
              >
                {isEditMode ? "Save Changes" : "That'll be all. Let's dive in!"}
              </Button>
            </div>
          </Box>
        </Box>
      </Container>
    </section>
  );
};

export default WhatYourName;
